import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import type { Person } from '../lib/types'

export interface PeopleEditorProps {
  householdId: string
  /** true when the viewer may not write this household's people */
  readOnly?: boolean
}

const MS_PER_YEAR = 31557600000

/**
 * Organizers think in ages; the DB stores birthdates.
 *
 * `null` in, `null` out: `person.birthdate` is nullable and NULL means **adult**
 * (see 20260808020000_person_birthdate_nullable.sql). It is not "unknown" and it
 * is not a prompt to go and ask — the intake form only collects ages for under-18s.
 */
export function ageFromBirthdate(birthdate: string | null): number | null {
  if (!birthdate) return null
  const ms = Date.parse(birthdate)
  if (Number.isNaN(ms)) return null
  return Math.floor((Date.now() - ms) / MS_PER_YEAR)
}

/** What to show next to a name. NULL birthdate reads as "adult", never as a gap. */
export function describeAge(birthdate: string | null): string {
  if (!birthdate) return 'adult'
  const age = ageFromBirthdate(birthdate)
  return age === null ? birthdate : `${age} yrs`
}

const css = `
.spz-people { display: flex; flex-direction: column; gap: 0.6rem; }
.spz-people ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.spz-people li { display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.4rem 0.6rem; padding: 0.5rem 0; border-bottom: 1px solid color-mix(in srgb, currentColor 10%, transparent); }
.spz-people .spz-people-name { font-weight: 600; }
.spz-people .spz-people-meta { color: var(--muted); font-size: 0.85rem; }
.spz-people .spz-people-spacer { flex: 1 1 auto; }
.spz-people .spz-people-empty { color: var(--muted); font-size: 0.9rem; }
.spz-people form { display: flex; flex-direction: column; gap: 0.5rem; padding: 0.6rem 0; }
.spz-people label { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.8rem; color: var(--muted); }
.spz-people input { min-height: 2.75rem; padding: 0.5rem 0.7rem; font: inherit; color: inherit; background: transparent; border: 1px solid color-mix(in srgb, currentColor 25%, transparent); border-radius: 0.5rem; width: 100%; }
.spz-people .spz-people-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.spz-people button { min-height: 2.75rem; padding: 0.5rem 0.9rem; font: inherit; border-radius: 0.5rem; border: 1px solid color-mix(in srgb, currentColor 30%, transparent); background: transparent; color: inherit; cursor: pointer; }
.spz-people button.spz-primary { border-color: var(--accent); background: var(--accent); color: var(--ground); font-weight: 600; }
.spz-people button:disabled { opacity: 0.55; cursor: default; }
.spz-people .spz-people-error { color: #b3261e; font-size: 0.85rem; }
`

interface Draft {
  name: string
  /** '' means "leave it NULL", i.e. an adult. */
  birthdate: string
  notes: string
}

const emptyDraft: Draft = { name: '', birthdate: '', notes: '' }

/** Every column of Person — a partial select would make the cast below a lie. */
const PERSON_COLUMNS = 'id, household_id, name, birthdate, notes, created_at'

/**
 * People CRUD for one household. Every read and write goes through the anon
 * client so RLS is exercised rather than bypassed.
 */
export default function PeopleEditor({ householdId, readOnly = false }: PeopleEditorProps) {
  const [people, setPeople] = useState<Person[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(emptyDraft)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('person')
      .select(PERSON_COLUMNS)
      .eq('household_id', householdId)
      // NULL birthdates (the adults) sort last, so the children group together.
      .order('birthdate', { ascending: true, nullsFirst: false })
    if (err) setError(err.message)
    else {
      setError(null)
      setPeople((data ?? []) as unknown as Person[])
    }
    setLoading(false)
  }, [householdId])

  useEffect(() => {
    void load()
  }, [load])

  function startAdd() {
    setEditingId(null)
    setDraft(emptyDraft)
    setAdding(true)
    setError(null)
  }

  function startEdit(person: Person) {
    setAdding(false)
    setEditingId(person.id)
    setDraft({
      name: person.name,
      birthdate: person.birthdate ?? '',
      notes: person.notes ?? '',
    })
    setError(null)
  }

  function cancel() {
    setAdding(false)
    setEditingId(null)
    setDraft(emptyDraft)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const name = draft.name.trim()
    if (!name) {
      setError('Name is required')
      return
    }

    setBusy(true)
    setError(null)
    // Birthdate is optional and NULL means adult — that is the common case, so an
    // empty field is a valid answer, not a validation failure.
    const payload = {
      name,
      birthdate: draft.birthdate ? draft.birthdate : null,
      notes: draft.notes.trim() || null,
    }
    const { error: err } = editingId
      ? await supabase.from('person').update(payload).eq('id', editingId)
      : await supabase.from('person').insert({ ...payload, household_id: householdId })
    setBusy(false)

    if (err) {
      setError(err.message)
      return
    }
    cancel()
    await load()
  }

  async function remove(person: Person) {
    if (!window.confirm(`Remove ${person.name}?`)) return
    setBusy(true)
    const { error: err } = await supabase.from('person').delete().eq('id', person.id)
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    await load()
  }

  const editing = adding || editingId !== null

  return (
    <div className="spz-people">
      <style>{css}</style>

      {loading ? (
        <p className="spz-people-empty">Loading people&hellip;</p>
      ) : people.length === 0 ? (
        <p className="spz-people-empty">No people yet.</p>
      ) : (
        <ul>
          {people.map((person) => (
            <li key={person.id}>
              <span className="spz-people-name">{person.name}</span>
              <span className="spz-people-meta">
                {describeAge(person.birthdate)}
                {person.notes ? ` · ${person.notes}` : ''}
              </span>
              <span className="spz-people-spacer" />
              {!readOnly && (
                <>
                  <button type="button" onClick={() => startEdit(person)} disabled={busy}>
                    Edit
                  </button>
                  <button type="button" onClick={() => void remove(person)} disabled={busy}>
                    Remove
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="spz-people-error" role="alert">
          {error}
        </p>
      )}

      {!readOnly && editing && (
        <form onSubmit={(e) => void submit(e)}>
          <label>
            Name
            <input
              type="text"
              required
              value={draft.name}
              autoComplete="off"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label>
            Birthdate &mdash; only for under-18s. Leave blank for an adult.
            <input
              type="date"
              value={draft.birthdate}
              onChange={(e) => setDraft({ ...draft, birthdate: e.target.value })}
            />
          </label>
          <label>
            Notes (optional)
            <input
              type="text"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </label>
          <div className="spz-people-actions">
            <button type="submit" className="spz-primary" disabled={busy}>
              {editingId ? 'Save' : 'Add person'}
            </button>
            <button type="button" onClick={cancel} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {!readOnly && !editing && (
        <div className="spz-people-actions">
          <button type="button" onClick={startAdd} disabled={busy}>
            + Add person
          </button>
        </div>
      )}
    </div>
  )
}
